"use client";
import Image from '@/components/ui/MediaImage';
import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { transformMediaUrl } from "@/lib/media-url-transformer";
import { shouldEmbedBlockPropsInDom } from "@/lib/embed-block-props-dom";
import { sanitizeCmsHtml } from "@/lib/sanitize-cms-html";

interface WithImageCtaProps {
    image?: { mediaId?: number; url?: string; alt?: string };
    title?: string;
    description?: string;
    quote?: string | null;
}

export default function WithImageCta({ 
    image,
    title = 'Explore Every Corner Of The World With Us.',
    description = 'Join us on an extraordinary journey as we explore every corner of the world together. Our adventures will take you to hidden gems, iconic landmarks, and breathtaking landscapes, offering unforgettable experiences and memories. Discover the beauty and diversity of our planet with us.',
    quote = 'Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem Ipsum has been the industry\'s standard dummy text ever since the 1500s.',
}: WithImageCtaProps) {
    const [componentProps, setComponentProps] = useState<WithImageCtaProps>({});

    // Component hydration: data-component-props'tan props'ları al
    useEffect(() => {
        if (!shouldEmbedBlockPropsInDom()) return;
        const updateProps = () => {
            const element = document.querySelector('[data-component-path="sections/WithImageCta"]');
            if (element) {
                const propsAttr = element.getAttribute('data-component-props');
                if (propsAttr) {
                    try {
                        const props = JSON.parse(propsAttr);
                        setComponentProps(props);
                    } catch {
                        // Props parse hatası
                    }
                }
            }
        };

        updateProps();

        const observer = new MutationObserver(updateProps);
        const element = document.querySelector('[data-component-path="sections/WithImageCta"]');
        if (element) {
            observer.observe(element, {
                attributes: true,
                attributeFilter: ['data-component-props']
            });
        }

        return () => {
            observer.disconnect();
        };
    }, []);

    // Props'tan veya componentProps'tan değerleri al
    const finalImage = componentProps.image || image;
    const finalTitle = componentProps.title || title;
    const finalDescription = componentProps.description || description;
    const finalQuote = componentProps.quote !== undefined ? componentProps.quote : quote;
    
    // URL'i transform et (medya proxy kullanımı için)
    const imageUrl = finalImage?.url ? transformMediaUrl(finalImage.url, null) : null;
    
    // GrapesJS'de görünmesi için default değer var, ama gerçek render'da sadece veri varsa göster
    // Eğer componentProps.quote undefined ise -> GrapesJS preview, default quote'u göster
    // Eğer componentProps.quote tanımlıysa -> Gerçek render, sadece değer varsa göster
    const isInGrapesJS = componentProps.quote === undefined;
    const shouldShowQuote = isInGrapesJS 
        ? Boolean(finalQuote?.trim()) // GrapesJS: default değeri göster (varsa)
        : Boolean(finalQuote && finalQuote.trim()); // Gerçek render: sadece veri varsa göster

    return (
        <div className="flex flex-col my-[190px]">
            <div className="flex flex-row gap-14 items-start">
                <motion.div 
                    className="flex-1 flex flex-col justify-between gap-16"
                    initial={{ opacity: 0, x: -50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ 
                        type: "spring" as const, 
                        stiffness: 80, 
                        damping: 20,
                        delay: 0.2
                    }}
                >
                    <motion.h2 
                        className="font-bold text-alsero-second text-2xl sm:text-3xl md:text-4xl lg:text-5xl leading-tight sm:leading-snug"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.3, duration: 0.6 }}
                        data-prop-path="title"
                    >
                        {finalTitle}
                    </motion.h2>
                    {finalDescription && (
                        <motion.div 
                            className="text-alsero-light-gray text-base sm:text-lg md:text-xl leading-relaxed prose prose-sm sm:prose-base max-w-none"
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.5, duration: 0.6 }}
                            data-prop-path="description"
                            dangerouslySetInnerHTML={{ __html: sanitizeCmsHtml(finalDescription) }}
                        />
                    )}
                </motion.div>
                {imageUrl && (
                    <motion.div 
                        className="flex-1 shadow-img p-2 bg-white"
                        initial={{ opacity: 0, x: 50, scale: 0.95 }}
                        whileInView={{ opacity: 1, x: 0, scale: 1 }}
                        viewport={{ once: true, amount: 0.3 }}
                        transition={{ 
                            type: "spring" as const, 
                            stiffness: 80, 
                            damping: 20,
                            delay: 0.3
                        }}
                        whileHover={{ rotate: -1 }}
                    >
                        <motion.div
                            className="overflow-hidden"
                        >
                            <Image
                                src={imageUrl}
                                alt={finalImage?.alt || "image"}
                                width={814}
                                height={550}
                                className="w-full h-[550px] object-cover"
                                unoptimized={imageUrl.startsWith('/api/media/proxy/')}
                            />
                        </motion.div>
                    </motion.div>
                )}
            </div>
            {shouldShowQuote && finalQuote && (
                <motion.div 
                className="w-1/2 bg-white border-l-[3px] border-alsero-first py-5 ps-6 pe-8 self-center border -mt-[175px] relative z-10"
                initial={{ opacity: 0, y: 30, scale: 0.95 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true }}
                transition={{ 
                    type: "spring" as const, 
                    stiffness: 100, 
                    damping: 20,
                    delay: 0.6
                }}
                whileHover={{ 
                    scale: 1.02,
                    borderColor: "rgba(227, 30, 36, 1)",
                    boxShadow: "0 10px 30px rgba(227, 30, 36, 0.1)"
                }}
                >
                <p className="text-lg text-alsero-light-gray" data-prop-path="quote"><i>{finalQuote}</i></p>
                </motion.div>
            )}
        </div>
    )
}